import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  microgroupOnboardingPasswordSchema,
  type MicrogroupOnboardingPasswordInput,
  type MicrogroupOnboardingTokenInfo,
  DEFAULT_ONBOARDING_CONSENT_TYPES,
} from "../../schemas/microgroupOnboarding";
import {
  CONSENT_TYPES,
  CONSENT_TYPE_DESCRIPTIONS,
  CONSENT_TYPE_LABELS,
  type ConsentType,
} from "../../schemas/consent";
import { sanitize } from "../custom/ConsentStepper";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Button } from "@/components/base/buttons/button";
import { InputBase } from "@/components/base/input/input";
import { Progress as ProgressSteps } from "@/components/application/progress-steps/progress-steps";

/**
 * UC-006 MicrogroupOnboardingStepper — 5-step Wizard na oficiálních Untitled UI
 * komponentách (Progress steps, Button, Input, Alert). Logika beze změny:
 *   1. Set password (NIST SP 800-63B min 14 znaků).
 *   2. Read consent text (markdown HTML, sanitized DOMPurify).
 *   3. Acknowledge.
 *   4. Accept → onSubmit().
 *   5. Success.
 */

const STEPS = ["password", "read", "acknowledge", "accept", "success"] as const;
type StepName = (typeof STEPS)[number];

const STEP_LABELS: Record<StepName, string> = {
  password: "Vytvořte si heslo",
  read: "Přečtěte si podmínky",
  acknowledge: "Potvrďte, že rozumíte",
  accept: "Udělte souhlas",
  success: "Hotovo",
};

type StepStatus = "incomplete" | "current" | "complete";

const CONSENT_TEXT_CLASSES =
  "max-h-[400px] overflow-y-auto rounded-xl border border-secondary bg-primary p-4 text-sm leading-relaxed text-primary " +
  "[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-0 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 " +
  "[&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_a]:text-brand-secondary [&_a]:underline";

const HEADING_CLASSES = "text-lg font-semibold text-primary m-0 outline-none";

export interface MicrogroupOnboardingStepperProps {
  tokenInfo: MicrogroupOnboardingTokenInfo;
  onSubmit: (input: {
    password: string;
    consent_text_version: string;
    consent_types: string[];
  }) => Promise<void>;
  onCompleted?: () => void;
  welcomeHeading?: string;
}

export function MicrogroupOnboardingStepper({
  tokenInfo,
  onSubmit,
  onCompleted,
  welcomeHeading,
}: MicrogroupOnboardingStepperProps): ReactElement {
  const [step, setStep] = useState<StepName>("password");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const passwordForm = useForm<MicrogroupOnboardingPasswordInput>({
    resolver: zodResolver(microgroupOnboardingPasswordSchema),
    defaultValues: { password: "", passwordConfirm: "" },
    mode: "onBlur",
  });

  const stepIdx = STEPS.indexOf(step);

  const sanitizedHtml = useMemo(
    () => sanitize(tokenInfo.consent_text_html),
    [tokenInfo.consent_text_html],
  );

  const requestedConsentTypes = useMemo(
    () =>
      DEFAULT_ONBOARDING_CONSENT_TYPES.filter((t) =>
        (CONSENT_TYPES as readonly string[]).includes(t),
      ),
    [],
  );

  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const goBack = useCallback(() => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) {
      setStep(STEPS[idx - 1]!);
      setSubmitError(null);
    }
  }, [step]);

  const handlePasswordSubmit = passwordForm.handleSubmit(() => {
    setStep("read");
  });

  const handleSubmitAccept = useCallback(async () => {
    setSubmitError(null);
    const password = passwordForm.getValues("password");
    if (password.length < 14) {
      setSubmitError("Heslo nesplňuje minimální délku 14 znaků.");
      setStep("password");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        password,
        consent_text_version: tokenInfo.consent_text_version,
        consent_types: requestedConsentTypes,
      });
      setStep("success");
      onCompleted?.();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Aktivace selhala.");
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, onCompleted, passwordForm, requestedConsentTypes, tokenInfo.consent_text_version]);

  return (
    <div
      role="region"
      aria-label="Aktivace účtu mikroskupiny"
      data-testid="onboarding-stepper"
      className="bg-primary border border-secondary rounded-xl shadow-xs p-6 flex flex-col gap-5"
    >
      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-bold text-primary m-0">
          {welcomeHeading ?? `Vítejte v mikroskupině ${tokenInfo.microgroup_name}`}
        </h1>
        <p className="text-sm text-tertiary m-0">
          Aktivujte si účet — nastavte si heslo a přijměte souhlasy.
        </p>
        <ProgressSteps.IconsWithText
          type="number"
          orientation="horizontal"
          size="sm"
          className="mt-2"
          items={STEPS.map((s, idx) => ({
            title: STEP_LABELS[s],
            description: "",
            status: (idx < stepIdx
              ? "complete"
              : idx === stepIdx
                ? "current"
                : "incomplete") as StepStatus,
          }))}
        />
      </header>

      <div className="min-h-[12rem]">
        {step === "password" && (
          <PasswordStep form={passwordForm} onSubmit={handlePasswordSubmit} headingRef={headingRef} />
        )}
        {step === "read" && (
          <ReadStep
            consentTextVersion={tokenInfo.consent_text_version}
            sanitizedHtml={sanitizedHtml}
            headingRef={headingRef}
          />
        )}
        {step === "acknowledge" && (
          <AcknowledgeStep acknowledged={acknowledged} onChange={setAcknowledged} headingRef={headingRef} />
        )}
        {step === "accept" && (
          <AcceptStep
            consentTextVersion={tokenInfo.consent_text_version}
            requestedTypes={requestedConsentTypes}
            error={submitError}
            headingRef={headingRef}
          />
        )}
        {step === "success" && <SuccessStep headingRef={headingRef} />}
      </div>

      <div
        className="flex flex-wrap items-center gap-3 border-t border-secondary pt-4"
        role="group"
        aria-label="Akce"
      >
        {step !== "password" && step !== "success" && (
          <Button color="secondary" onPress={goBack} isDisabled={submitting} data-testid="onboarding-back">
            Zpět
          </Button>
        )}
        {step === "password" && (
          <Button
            color="primary"
            className="ml-auto"
            onPress={() => handlePasswordSubmit()}
            data-testid="onboarding-password-submit"
          >
            Pokračovat
          </Button>
        )}
        {step === "read" && (
          <Button
            color="primary"
            className="ml-auto"
            onPress={() => setStep("acknowledge")}
            data-testid="onboarding-read-next"
          >
            Pokračovat
          </Button>
        )}
        {step === "acknowledge" && (
          <Button
            color="primary"
            className="ml-auto"
            onPress={() => setStep("accept")}
            isDisabled={!acknowledged}
            data-testid="onboarding-ack-next"
          >
            Pokračovat
          </Button>
        )}
        {step === "accept" && (
          <Button
            color="primary"
            className="ml-auto"
            onPress={handleSubmitAccept}
            isLoading={submitting}
            data-testid="onboarding-submit"
          >
            Aktivovat účet
          </Button>
        )}
      </div>
    </div>
  );
}

interface PasswordStepProps {
  form: ReturnType<typeof useForm<MicrogroupOnboardingPasswordInput>>;
  onSubmit: () => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

function PasswordStep({ form, onSubmit, headingRef }: PasswordStepProps): ReactElement {
  return (
    <section aria-labelledby="onb-password-heading" className="flex flex-col gap-3">
      <h2 id="onb-password-heading" ref={headingRef} tabIndex={-1} className={HEADING_CLASSES}>
        {STEP_LABELS.password}
      </h2>
      <p className="text-sm text-tertiary m-0">
        Heslo musí mít alespoň <strong>14 znaků</strong>. Žádná pravidla na speciální
        znaky — preferujte délku.
      </p>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-primary" htmlFor="onb-pw">
            Heslo <span aria-hidden="true">*</span>
          </label>
          <InputBase
            id="onb-pw"
            type="password"
            size="md"
            autoComplete="new-password"
            data-testid="onboarding-password"
            isInvalid={Boolean(form.formState.errors.password)}
            {...form.register("password")}
          />
          {form.formState.errors.password?.message && (
            <p role="alert" className="text-sm text-error-primary">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-primary" htmlFor="onb-pw-confirm">
            Heslo znovu <span aria-hidden="true">*</span>
          </label>
          <InputBase
            id="onb-pw-confirm"
            type="password"
            size="md"
            autoComplete="new-password"
            data-testid="onboarding-password-confirm"
            isInvalid={Boolean(form.formState.errors.passwordConfirm)}
            {...form.register("passwordConfirm")}
          />
          {form.formState.errors.passwordConfirm?.message && (
            <p role="alert" className="text-sm text-error-primary">
              {form.formState.errors.passwordConfirm.message}
            </p>
          )}
        </div>
      </form>
    </section>
  );
}

interface ReadStepProps {
  consentTextVersion: string;
  sanitizedHtml: string;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

function ReadStep({ consentTextVersion, sanitizedHtml, headingRef }: ReadStepProps): ReactElement {
  return (
    <section aria-labelledby="onb-read-heading" className="flex flex-col gap-3">
      <h2 id="onb-read-heading" ref={headingRef} tabIndex={-1} className={HEADING_CLASSES}>
        {STEP_LABELS.read} (verze {consentTextVersion})
      </h2>
      <article
        className={CONSENT_TEXT_CLASSES}
        // sanitizedHtml already passed through DOMPurify.
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </section>
  );
}

interface AcknowledgeStepProps {
  acknowledged: boolean;
  onChange: (v: boolean) => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

function AcknowledgeStep({ acknowledged, onChange, headingRef }: AcknowledgeStepProps): ReactElement {
  return (
    <section aria-labelledby="onb-ack-heading" className="flex flex-col gap-3">
      <h2 id="onb-ack-heading" ref={headingRef} tabIndex={-1} className={HEADING_CLASSES}>
        {STEP_LABELS.acknowledge}
      </h2>
      <label className="flex items-start gap-3 p-4 rounded-xl border border-secondary bg-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onChange(e.target.checked)}
          data-testid="onboarding-ack-checkbox"
          className="mt-0.5 size-4 shrink-0 accent-brand-600"
        />
        <span className="text-sm text-primary">
          Potvrzuji, že jsem si přečetl(a) podmínky a rozumím jim.
        </span>
      </label>
    </section>
  );
}

interface AcceptStepProps {
  consentTextVersion: string;
  requestedTypes: readonly string[];
  error: string | null;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

function AcceptStep({ consentTextVersion, requestedTypes, error, headingRef }: AcceptStepProps): ReactElement {
  return (
    <section aria-labelledby="onb-accept-heading" className="flex flex-col gap-4">
      <h2 id="onb-accept-heading" ref={headingRef} tabIndex={-1} className={HEADING_CLASSES}>
        {STEP_LABELS.accept}
      </h2>
      <p className="text-sm text-tertiary m-0">
        Kliknutím na „Aktivovat účet" udělujete následující souhlasy (verze textu{" "}
        {consentTextVersion}):
      </p>
      <ul className="flex flex-col gap-2">
        {requestedTypes.map((t) => {
          const key = t as ConsentType;
          const label = CONSENT_TYPE_LABELS[key] ?? t;
          const desc = CONSENT_TYPE_DESCRIPTIONS[key];
          return (
            <li key={t} className="flex items-start gap-2 text-sm">
              <span className="text-success-primary mt-0.5" aria-hidden="true">
                ✓
              </span>
              <span className="text-primary">
                <strong>{label}</strong>
                {desc && <span className="block text-tertiary">{desc}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {error && (
        <InlineAlert variant="error" title="Chyba">
          {error}
        </InlineAlert>
      )}
    </section>
  );
}

function SuccessStep({
  headingRef,
}: { headingRef: React.RefObject<HTMLHeadingElement | null> }): ReactElement {
  return (
    <section aria-labelledby="onb-success-heading" className="flex flex-col gap-2">
      <h2 id="onb-success-heading" ref={headingRef} tabIndex={-1} className={HEADING_CLASSES}>
        Vítejte!
      </h2>
      <p className="text-tertiary m-0">Účet je aktivní. Přesměrovávám na váš přehled…</p>
    </section>
  );
}

/** Exported pro testy. */
export const ONBOARDING_STEPS = STEPS;
